import React, {
  useState,
  useEffect,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  MenuItem,
  type SelectChangeEvent,
  Card,
  Divider,
  useTheme,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import CustomTextField from "../../../components/ui/Textfield";
import PhoneNumberField from "../../../components/ui/PhoneNumberField";
import PhotoEditor from "../../../components/PhotoEditor";
import CountrySelector from "../../../components/ui/CountrySelector";
import { MuiTelInputInfo } from "mui-tel-input";
import {
  CUSTOM_SALUTATION_VALUE,
  SALUTATIONS,
} from "../../../constants/salutations";

interface IContactData {
  contact_type: "individual";
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  country: string;
  website: string;
  notes: string;
  facebook_profile: string;
  instagram_profile: string;
  twitter_profile: string;
  linkedin_profile: string;
  contact_image: string | null;
  contactImageFile: File | null;
}

interface IServerResponse {
  success: boolean;
  message: string;
}

interface IContactResponse {
  id: number;
  [key: string]: unknown;
}

interface IAddIndividualProps {
  onContactAdded?: (contact: IContactData & { id: number }) => void;
}

const getErrorMessage = (err: unknown, fallback: string) => {
  const responseData = (
    err as { response?: { data?: Record<string, unknown> } }
  )?.response?.data;
  if (!responseData || typeof responseData !== "object") {
    return fallback;
  }
  return Object.values(responseData).flat().join(" ") || fallback;
};

const AddIndividualContact: React.FC<IAddIndividualProps> = ({
  onContactAdded,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setServerResponse] = useState<IServerResponse | null>(null);
  const [emailError, setEmailError] = useState("");
  const [isCustomSalutation, setIsCustomSalutation] = useState(false);

  const [form, setForm] = useState<IContactData>({
    contact_type: "individual",
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    dob: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "",
    country: "",
    website: "",
    notes: "",
    facebook_profile: "",
    instagram_profile: "",
    twitter_profile: "",
    linkedin_profile: "",
    contact_image: null,
    contactImageFile: null,
  });

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  useEffect(() => {
    return () => {
      if (form.contact_image && form.contact_image.startsWith("blob:")) {
        URL.revokeObjectURL(form.contact_image);
      }
    };
  }, [form.contact_image]);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
      setForm((prev) => ({ ...prev, email: value }));
    } else {
      setForm((prev) => ({ ...prev, [name as keyof IContactData]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!form.first_name || !form.last_name) {
      setServerResponse({
        success: false,
        message: "First name and last name are required.",
      });
      showErrorToast("First name and last name are required.");
      setIsSubmitting(false);
      return;
    }

    if (!form.email && !form.phone) {
      setServerResponse({
        success: false,
        message: "Provide at least one contact method (email or phone number).",
      });
      showErrorToast(
        "Provide at least one contact method (email or phone number).",
      );
      setIsSubmitting(false);
      return;
    }

    if (emailError) {
      setServerResponse({
        success: false,
        message: "Please correct the email address error.",
      });
      showErrorToast("Please correct the email address error.");
      setIsSubmitting(false);
      return;
    }
    const normalizeUrl = (value: string) =>
      value && !value.startsWith("http://") && !value.startsWith("https://")
        ? `https://${value}`
        : value;

    const normalizedForm = {
      ...form,
      website: normalizeUrl(form.website),
      facebook_profile: normalizeUrl(form.facebook_profile),
      linkedin_profile: normalizeUrl(form.linkedin_profile),
      twitter_profile: normalizeUrl(form.twitter_profile),
      instagram_profile: normalizeUrl(form.instagram_profile),
    };

    const formData = new FormData();
    formData.append("contact_type", normalizedForm.contact_type);
    Object.entries(normalizedForm).forEach(([key, value]) => {
      if (key === "contactImageFile" && value instanceof File) {
        formData.append("contact_image", value);
      } else if (key !== "contact_image" && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    try {
      const response = await apiService.post<IContactResponse>(
        "/api/contacts/contact/",
        formData,
        false,
      );
      setServerResponse({
        success: true,
        message: "Individual contact added successfully.",
      });
      showSuccessToast("Individual contact added successfully.");

      if (onContactAdded) {
        onContactAdded({ ...form, id: response.data.id });
      }

      setTimeout(() => {
        navigate(`/contacts/individual/view/${response.data.id}`);
      }, 2000);
    } catch (err: unknown) {
      console.error("Failed to add individual contact:", err);
      const errorMessage = getErrorMessage(
        err,
        "Failed to add individual contact. Please try again.",
      );
      setServerResponse({
        success: false,
        message: errorMessage,
      });
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailValid = !emailError && form.email.includes("@");

  const handlePhoneInputChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleSalutationChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;
    const isCustom = value === CUSTOM_SALUTATION_VALUE;
    setIsCustomSalutation(isCustom);
    setForm((prev) => ({ ...prev, salutation: isCustom ? "" : value }));
  };

  const handleSaveContactImage = async (
    imageData: string,
    contactId?: number,
  ) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `contact_image_${contactId || "new"}.jpg`, {
        type: blob.type,
      });
      setForm((prev) => ({
        ...prev,
        contact_image: imageData,
        contactImageFile: file,
      }));
      showSuccessToast("Contact image saved successfully!");
    } catch (error: unknown) {
      console.error("Failed to process contact image:", error);
      showErrorToast("Failed to process contact image. Please try again.");
    }
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": {
        borderColor: colors.primary[300],
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary[400],
      },
    },
    "& .MuiInputLabel-root": {
      color: colors.primary[300],
    },
    "& .MuiInputBase-input": {
      color: colors.primary[700],
    },
  };

  return (
    <Card
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: 4,
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        background: colors.neutral[900],
        borderTop: `5px solid ${colors.neutral[300]}`,
        borderTopWidth: 5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: colors.neutral[300],
            fontWeight: 700,
          }}
        >
          Add Individual Contact
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Personal Details Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Personal Details
          </Typography>
          <Grid container spacing={3}>
            {/* Left column: Profile Photo */}
            <Grid size={{ xs: 12, md: 4 }}>
              <PhotoEditor
                initialImage={form.contact_image}
                title="Profile Photo"
                onSave={(imageData: string) => handleSaveContactImage(imageData)}
              />
            </Grid>

            {/* Right side: remaining 2 columns, fields wrap 2-per-row */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    select
                    label="Salutation"
                    name="salutation"
                    value={isCustomSalutation ? CUSTOM_SALUTATION_VALUE : form.salutation}
                    onChange={handleSalutationChange}
                    sx={textFieldStyles}
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        PaperProps: { sx: { maxHeight: 280, mt: 0.5 } },
                      },
                    }}
                  >
                    <MenuItem value="">No salutation</MenuItem>
                    {SALUTATIONS.map((salutation) => (
                      <MenuItem key={salutation} value={salutation}>
                        {salutation}
                      </MenuItem>
                    ))}
                    <MenuItem value={CUSTOM_SALUTATION_VALUE}>Other</MenuItem>
                  </CustomTextField>
                </Grid>
                {isCustomSalutation && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <CustomTextField
                      fullWidth
                      label="Custom salutation"
                      name="salutation"
                      value={form.salutation}
                      onChange={handleChange}
                      placeholder="Enter salutation"
                      required
                      sx={textFieldStyles}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    error={!!emailError}
                    helperText={emailError}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isEmailValid && (
                            <Check sx={{ color: colors.primary[400] }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...textFieldStyles,
                      "& .MuiFormHelperText-root": { color: colors.neutral[100] },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PhoneNumberField
                    fullWidth
                    label="Phone Number"
                    value={form.phone}
                    onChange={handlePhoneInputChange}
                    defaultCountry="ZW"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={form.dob}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split("T")[0] }}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="Website"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Location Info Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Location Info
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 1"
                name="address_one"
                value={form.address_one}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 2"
                name="address_two"
                value={form.address_two}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                sx={textFieldStyles}
                select
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {reduxTheme.cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Suburb"
                name="suburb"
                value={form.suburb}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            
          <Grid size={{ xs: 12, md: 4 }}>
            <CountrySelector
              value={form.country}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, country: value }))
              }
              label="Country"
              sx={textFieldStyles}
            />
          </Grid>

          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Social Media Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Social Media
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Facebook Profile"
                name="facebook_profile"
                value={form.facebook_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="LinkedIn Profile"
                name="linkedin_profile"
                value={form.linkedin_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="X Profile"
                name="twitter_profile"
                value={form.twitter_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Instagram Profile"
                name="instagram_profile"
                value={form.instagram_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Additional Info Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Additional Info
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <CustomTextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={form.notes}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 4 }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: colors.primary[400],
              "&:hover": {
                backgroundColor: colors.primary[300],
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: colors.neutral[100] }} />
            ) : (
              "Save Contact"
            )}
          </Button>
        </Box>
      </form>
    </Card>
  );
};

export default AddIndividualContact;